import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { brandName, publicAsset } from "./careData";
import { Footer, Logo } from "../../components/care/CareLayout";

export function PublicNav() {
  return (
    <header className="topbar public-topbar">
      <Logo />
      <nav className="public-nav" aria-label="Main navigation">
        <a href="#operations">Care Operations</a>
        <a href="#assessments">Assessments</a>
        <a href="#staff">Staff Operations</a>
        <a href="#workspace">Workspace Admin</a>
        <a href="#pricing">Pricing</a>
      </nav>
      <div className="topbar-actions">
        <Link className="ghost-link" to="/login">Sign In</Link>
        <Link className="btn secondary demo-cta" to="/book-a-demo">
          <i className="ti ti-calendar-event" /> Book a Demo
        </Link>
        <Link className="btn primary" to="/get-started">Get Started</Link>
      </div>
    </header>
  );
}

export default function LandingPage() {
  const { isAuthenticated, loading } = useAuth();

  if (!loading && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const offerings = [
    ["ti-user-heart", "Resident Care Operations", <>Manage resident profiles, bio data, contacts, care history, care snapshots, care teams, documents, and daily care records.</>, "operations"],
    ["ti-clipboard-check", "Assessments and Care Plans", <>Run new resident onboarding, annual assessments, state assessment pages, behavioral notes, negotiated care plans, and final assessments.</>, "assessments"],
    ["ti-calendar-check", "Staff Schedules and Attendance", <>Create staff <strong>Schedules</strong>, block active overlaps, track <strong>Clocking In &amp; Out</strong>, review <strong>Timesheets</strong>, and keep <strong>Attendance</strong> status visible.</>, "staff"],
    ["ti-wallet", "Staff Payments", <>Manage staff payment context through <strong>Timesheets</strong>, charges, wallet tools, payment methods, and workspace billing records.</>, "staff-payments"],
    ["ti-user-search", "Find & Hire Care Givers", <>Support care staff onboarding and hiring workflows for caregivers, workers, and facility teams.</>, "hire-care-givers"],
    ["ti-notes", "Daily Notes and Documents", <>Capture daily notes with photos, preview documents, manage downloads, and keep internal or public files attached to the workspace.</>, "operations"],
    ["ti-building", "Organization Locations", <>Store facility location details with address, place ID, coordinates, and workspace-linked organization location selection.</>, "workspace"],
    ["ti-world", "Plans, Websites, and AI", <>Use subscriptions, payment methods, annual website benefits, and Premium AI for extraction, summaries, and care workflow drafts.</>, "pricing"],
  ];

  return (
    <div className="page public-page">
      <PublicNav />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <h1 className="care-landing-hero-title">Care facility operations and care worker tools in one workspace.</h1>
            <p>{brandName} helps Adult Family Homes, Assisted Living Facilities, care teams, and care workers manage residents, <strong className="care-landing-highlight-blue"><em>Assessments</em></strong>, <strong className="care-landing-highlight-blue"><em>Negotiated Care Plans</em></strong>, <strong className="care-landing-highlight-blue"><em>schedules</em></strong>, <strong className="care-landing-highlight-blue"><em>attendance</em></strong>, <strong className="care-landing-highlight-blue"><em>clocking in &amp; out</em></strong>, <strong className="care-landing-highlight-blue"><em>timesheets</em></strong>, daily notes, documents, billing, websites, and AI-assisted care workflows.</p>
            <div className="button-row">
              <Link className="btn primary" to="/get-started">Get Started</Link>
              <Link className="btn secondary" to="/login"><i className="ti ti-login" /> Sign In</Link>
              <Link className="btn secondary demo-cta" to="/book-a-demo">
                <i className="ti ti-calendar-event" /> Book a Demo
              </Link>
            </div>
          </div>
          <div className="hero-media" aria-label="Caregiver supporting an older adult">
            <img className="hero-photo" src={publicAsset("/care/care.png")} alt="Caregiver supporting an older adult" />
            <div className="floating-note"><i className="ti ti-shield-check" /><span>Resident care, staff operations, and AI-assisted documentation</span></div>
          </div>
        </section>

        <section className="public-section" id="operations">
          <h2>Current Olreadia Care offerings</h2>
          <div className="landing-card-grid">
            {offerings.map(([icon, title, text, anchor]) => (
              <Link className="landing-card" to="/get-started" id={anchor === "operations" ? undefined : anchor} key={title}>
                <span className="soft-icon"><i className={`ti ${icon}`} /></span>
                <div><h3>{title}</h3><p>{text}</p><i className="ti ti-arrow-right arrow" /></div>
              </Link>
            ))}
          </div>
          <div className="benefit-strip">
            <div><span><i className="ti ti-building-hospital" /></span><strong>Free Business</strong><p>Workspace setup, organization/facility profile, basic resident and staff directory, wallet, subscription management, and HIPAA-conscious AFH record organization.</p></div>
            <div><span><i className="ti ti-calendar-check" /></span><strong>Standard Business</strong><p>WAC 388-76 support, eMAR, ADL/task tracking, assessments, NCP workflow, CBHS supervision tracking, incident reporting, documents, RN delegation workflows, calendar, meal/activity planning, shifts, overtime, hours, and timesheet corrections.</p></div>
            <div><span><i className="ti ti-sparkles" /></span><strong>Premium Business</strong><p>Everything in Standard plus 1-click NCP generation, automatic ADL tasks, CBHS monthly reports, smart progress notes, compliance dashboard/alerts, compliance bundles, e-signatures, family portal, secure fax/email exchange, hours reports, and dispute workflows.</p></div>
          </div>
        </section>
      </main>
      <Footer publicLinks />
    </div>
  );
}
